import { useContext, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import FetchContext from 'context/fetch/FetchContext'
import useQueryLibrary from 'hooks/useQueryLibrary'
import { resolveCompanyFromLocation } from 'data-organizers/announcements/resolveCompanyFromLocation'
import { rootDataResponseHandler } from 'helpers/responseHandlers'

const JSON_HEADERS = { 'Content-Type': 'application/json' }

const SENDER_DETAIL_ID_KEY = 'announcements.senderDetailId'

const SENDER_NAMES_QUERY_KEY = ['announcements', 'send', 'sender-names']

const CONTACT_DETAILS_QUERY_KEY = ['announcements', 'send', 'contact-details']

const RECIPIENTS_QUERY_KEY = ['announcements', 'send', 'recipients']

export function rememberedSenderDetailId() {
    try {
        return window.localStorage.getItem(SENDER_DETAIL_ID_KEY)
    }
    catch {
        return null
    }
}

export function rememberSenderDetailId(senderDetailId) {
    try {
        window.localStorage.setItem(SENDER_DETAIL_ID_KEY, String(senderDetailId ?? ''))
    }
    catch {
        // Remembering a preference is not worth failing a send over.
    }
}

export default function useAnnouncementSendContext(announcement) {
    const fetcher = useContext(FetchContext)
    const queryLibrary = useQueryLibrary()
    const queryClient = useQueryClient()

    // The user stored at sign-in is the login response, which carries no firm; the
    // profile is where the server names it.
    const profile = useQuery({
        queryKey: queryLibrary.meQuery(),
        queryFn: () => fetcher.fetchUser(),
    })
    const firm = profile.data?.firm ?? null

    const [selectedCompanyId, setSelectedCompanyId] = useState(
        () => resolveCompanyFromLocation(window.location),
    )

    const companies = useQuery({
        queryKey: ['announcements', 'send', 'companies'],
        queryFn: () => fetcher.fetchCompaniesForCompaniesPage(''),
        enabled: firm != null,
    })

    const senderNames = useQuery({
        queryKey: SENDER_NAMES_QUERY_KEY,
        queryFn: () => fetcher.fetchOwnFirmSenderNames(),
        enabled: firm != null,
    })

    const contactDetails = useQuery({
        queryKey: CONTACT_DETAILS_QUERY_KEY,
        queryFn: () => fetcher.fetchOwnFirmContactDetails(),
        enabled: firm != null,
    })

    // Read from the announcement API rather than from the company's own details: which
    // addresses may receive marketing mail is settled by consent, on the server.
    const recipients = useQuery({
        queryKey: [...RECIPIENTS_QUERY_KEY, announcement.documentId, selectedCompanyId],
        queryFn: () => fetcher.authenticatedFetch({
            path: `/announcements/${announcement.documentId}/recipients`,
            params: { companyId: selectedCompanyId },
        }),
        enabled: firm != null && selectedCompanyId != null,
    })

    const companyQueryKey = queryLibrary.oneCompanyQuery(selectedCompanyId ?? '')
    const company = useQuery({
        queryKey: companyQueryKey,
        queryFn: () => fetcher.authenticatedFetch({
            path: `/companies/${selectedCompanyId}`,
            responseHandler: rootDataResponseHandler,
        }),
        enabled: firm != null && selectedCompanyId != null,
    })

    const send = useMutation({
        mutationFn: (request) => fetcher.authenticatedFetch({
            method: 'post',
            path: `/announcements/${announcement.documentId}/send`,
            data: {
                data: {
                    companyId: request.companyId,
                    recipientIds: request.recipientIds,
                    senderDetailId: request.senderDetailId,
                    contactDetailIds: request.contactDetailIds ?? [],
                },
            },
            headers: JSON_HEADERS,
        }),
    })

    return {
        firm,
        companies: companies.data ?? [],
        companyDetails: recipients.data ?? [],
        recipientsResolved: recipients.isSuccess,
        selectedCompanyId,
        senderNames: senderNames.data?.data ?? [],
        senderNamesEditable: senderNames.data?.meta?.editable ?? false,
        contactDetails: contactDetails.data?.data ?? [],
        contactDetailsResolved: contactDetails.isSuccess,
        contactDetailsEditable: contactDetails.data?.meta?.editable ?? false,
        contactDetailsQueryKey: CONTACT_DETAILS_QUERY_KEY,
        rememberedSenderDetailId: rememberedSenderDetailId(),
        onCompanySelected: setSelectedCompanyId,
        companyQueryKey,
        senderNamesQueryKey: SENDER_NAMES_QUERY_KEY,
        onDetailsChanged: async () => {
            await queryClient.invalidateQueries({ queryKey: RECIPIENTS_QUERY_KEY })
        },
        onSend: async (request) => {
            rememberSenderDetailId(request.senderDetailId)

            return send.mutateAsync(request)
        },
    }
}
